import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { LogOut, User as UserIcon, Menu, X, Settings, Crown } from "lucide-react";
import { useState, useEffect } from "react";
import { Emblem } from "./Emblem";

export default function Header() {
  const { user, logout, premium } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const navItem = ({ isActive }) =>
    `text-sm font-medium transition-colors px-3 py-2 rounded-xl ${
      isActive
        ? "text-white bg-white/5 border border-white/10"
        : "text-slate-400 hover:text-white hover:bg-white/5"
    }`;

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled ? "glass-header" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <Emblem className="h-9 w-9 transition-transform group-hover:rotate-3" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-white text-base tracking-tight">TestOpoGuardiaCivil</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-gcgold font-bold font-mono">Élite · Preparación</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {user ? (
            <>
              <NavLink to="/dashboard" className={navItem} data-testid="nav-dashboard">Panel</NavLink>
              <NavLink to="/tests" className={navItem} data-testid="nav-tests">Tests</NavLink>
              <NavLink to="/comunidad" className={navItem} data-testid="nav-comunidad">Comunidad</NavLink>
              <NavLink to="/stats" className={navItem} data-testid="nav-stats">Stats</NavLink>
              <NavLink to="/ranking" className={navItem} data-testid="nav-ranking">Ranking</NavLink>
              {premium?.is_premium ? (
                <NavLink to="/premium" className="text-sm font-bold text-gcgold px-3 py-2 rounded-xl bg-gcgold/10 border border-gcgold/30 flex items-center gap-1.5" data-testid="nav-premium-active">
                  <Crown className="h-3.5 w-3.5" />Premium
                </NavLink>
              ) : (
                <NavLink to="/premium" className="text-sm font-medium text-gcgold hover:text-gcgold-light px-3 py-2 rounded-xl hover:bg-gcgold/10 flex items-center gap-1.5" data-testid="nav-premium">
                  <Crown className="h-3.5 w-3.5" />Premium
                </NavLink>
              )}
              {user.role === "admin" && (
                <NavLink to="/admin" className={navItem} data-testid="nav-admin">
                  <Settings className="h-3.5 w-3.5 inline mr-1" />Admin
                </NavLink>
              )}
            </>
          ) : (
            <>
              <a href="/#categorias" className="text-sm font-medium text-slate-400 hover:text-white px-3 py-2 transition-colors" data-testid="nav-categories">Categorías</a>
              <a href="/#funciona" className="text-sm font-medium text-slate-400 hover:text-white px-3 py-2 transition-colors" data-testid="nav-how">Cómo funciona</a>
              <a href="/#features" className="text-sm font-medium text-slate-400 hover:text-white px-3 py-2 transition-colors">Características</a>
              <NavLink to="/premium" className="text-sm font-medium text-gcgold hover:text-gcgold-light px-3 py-2 rounded-xl hover:bg-gcgold/10 transition-colors flex items-center gap-1.5" data-testid="nav-premium-public">
                <Crown className="h-3.5 w-3.5" />Premium
              </NavLink>
            </>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link to="/profile" className="flex items-center gap-2 px-3 py-1.5 rounded-xl hover:bg-white/5 transition-colors" data-testid="profile-link">
                <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-gcgreen to-gcgreen-dark flex items-center justify-center">
                  <UserIcon className="h-3.5 w-3.5 text-gcgold" />
                </div>
                <span className="text-sm text-slate-200 max-w-[120px] truncate">{user.name?.split(" ")[0] || user.email}</span>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-white/5" data-testid="logout-button">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className="text-slate-300 hover:text-white hover:bg-white/5" data-testid="login-button">
                Acceder
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/register")}
                className="bg-gcgreen hover:bg-gcgreen-light text-white font-semibold shadow-[0_0_20px_-4px_rgba(10,92,54,0.6)] hover:shadow-[0_0_30px_-4px_rgba(10,92,54,0.9)] transition-all rounded-xl"
                data-testid="register-button"
              >
                Empezar gratis
              </Button>
            </>
          )}
        </div>

        <button className="md:hidden p-2 rounded-xl hover:bg-white/5 text-slate-300" onClick={() => setOpen(!open)} aria-label="Menú" data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-white/5" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {user ? (
              <>
                <NavLink to="/dashboard" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Panel</NavLink>
                <NavLink to="/tests" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Tests</NavLink>
                <NavLink to="/comunidad" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Comunidad</NavLink>
                <NavLink to="/stats" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Estadísticas</NavLink>
                <NavLink to="/ranking" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Ranking</NavLink>
                <NavLink to="/premium" onClick={() => setOpen(false)} className={`block text-sm py-2.5 px-3 rounded-xl flex items-center gap-2 ${premium?.is_premium ? "text-gcgold bg-gcgold/10 font-bold" : "text-gcgold hover:bg-gcgold/10"}`}><Crown className="h-3.5 w-3.5" />Premium {premium?.is_premium && "✓"}</NavLink>
                {user.role === "admin" && <NavLink to="/admin" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-gcgold hover:bg-white/5 rounded-xl">Admin</NavLink>}
                <NavLink to="/profile" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Perfil</NavLink>
                <button onClick={handleLogout} className="block w-full text-left text-sm py-2.5 px-3 text-red-400 hover:bg-red-900/20 rounded-xl">Salir</button>
              </>
            ) : (
              <>
                <a href="/#categorias" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Categorías</a>
                <a href="/#funciona" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Cómo funciona</a>
                <NavLink to="/premium" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-gcgold hover:bg-gcgold/10 rounded-xl flex items-center gap-2"><Crown className="h-3.5 w-3.5" />Premium</NavLink>
                <NavLink to="/login" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-xl">Acceder</NavLink>
                <NavLink to="/register" onClick={() => setOpen(false)} className="block text-sm py-2.5 px-3 text-gcgold font-semibold hover:bg-white/5 rounded-xl">Empezar gratis →</NavLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
